import React, { useState } from "react";
import Header from "../../components/header/Header";
import "../../css/ReviewForm.css";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/auth";
import { useNavigate } from "react-router-dom";

function ReviewForm() {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [auth] = useAuth();
    const navigate = useNavigate();
    const isLoggedIn = !!auth.token;
    const apiUrl = `${process.env.REACT_APP_API}`;

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validation
        if (rating === 0) {
            toast.error("Please select a rating");
            return;
        }

        if (comment.trim().length < 10) {
            toast.error("Please write at least 10 characters in your review");
            return;
        }

        setIsSubmitting(true);
        const name = auth.currentUser.name.first + " " + auth.currentUser.name.last;

        try {
            const response = await fetch(`${apiUrl}/review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ rating, comment, name })
            });

            if (response.ok) {
                toast.success("Thank you for your review! 🎉");
                setTimeout(() => navigate("/"), 1500);
            } else {
                console.log(response);
                toast.error("Could not submit review. Please try again.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStarMouseEnter = (index) => {
        setHoverRating(index + 1);
    };

    const handleStarMouseLeave = () => {
        setHoverRating(0);
    };

    const handleStarClick = (index) => {
        setRating(index + 1);
    };

    const getRatingText = () => {
        const currentRating = hoverRating || rating;
        switch(currentRating) {
            case 1: return "Poor";
            case 2: return "Fair";
            case 3: return "Good";
            case 4: return "Very Good";
            case 5: return "Excellent";
            default: return "Select your rating";
        }
    };

    return (
        <>
            <Header />
            {isLoggedIn ? (
                <div className="review-form-wrapper">
                    <div className="review-form-container">
                        <div className="review-form-card">
                            {/* Header Section */}
                            <div className="review-form-header">
                                <div className="review-icon-wrapper">
                                    <i className="fas fa-star review-main-icon"></i>
                                </div>
                                <h2 className="review-form-title">Share Your Experience</h2>
                                <p className="review-form-subtitle">
                                    We'd love to hear about your shopping experience
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="review-form">
                                {/* Rating Section */}
                                <div className="form-group-modern">
                                    <label className="form-label-modern">Your Rating</label>
                                    <div className="star-rating-container">
                                        <div className="star-rating-modern">
                                            {[0, 1, 2, 3, 4].map((index) => (
                                                <i
                                                    key={index}
                                                    className={`fas fa-star star-icon ${
                                                        (hoverRating || rating) > index ? "star-filled" : "star-empty"
                                                    }`}
                                                    onMouseEnter={() => handleStarMouseEnter(index)}
                                                    onMouseLeave={handleStarMouseLeave}
                                                    onClick={() => handleStarClick(index)}
                                                ></i>
                                            ))}
                                        </div>
                                        <span className={`rating-text ${(hoverRating || rating) > 0 ? 'rating-text-visible' : ''}`}>
                                            {getRatingText()}
                                        </span>
                                    </div>
                                </div>

                                {/* Comment Section */}
                                <div className="form-group-modern">
                                    <label htmlFor="comment" className="form-label-modern">
                                        Your Review
                                    </label>
                                    <textarea
                                        id="comment"
                                        className="form-textarea-modern"
                                        value={comment}
                                        onChange={(event) => setComment(event.target.value)}
                                        placeholder="Tell us about your experience with our products and service..."
                                        rows="6"
                                        maxLength="1000"
                                    ></textarea>
                                    <div className="character-count">
                                        {comment.length}/1000 characters
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn-secondary-modern"
                                        onClick={() => navigate("/")}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary-modern"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane me-2"></i>
                                                Submit Review
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Trust Indicators */}
                            <div className="review-trust-indicators">
                                <div className="trust-item">
                                    <i className="fas fa-shield-alt"></i>
                                    <span>Your review is anonymous</span>
                                </div>
                                <div className="trust-item">
                                    <i className="fas fa-check-circle"></i>
                                    <span>Verified purchase</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="login-prompt-wrapper">
                    <div className="login-prompt-card">
                        <div className="login-prompt-icon">
                            <i className="fas fa-lock"></i>
                        </div>
                        <h3 className="login-prompt-title">Authentication Required</h3>
                        <p className="login-prompt-text">
                            Please log in to your account to share your review
                        </p>
                        <button
                            className="btn-login-modern"
                            onClick={() => navigate("/login")}
                        >
                            <i className="fas fa-sign-in-alt me-2"></i>
                            Log In to Continue
                        </button>
                        <p className="signup-prompt">
                            Don't have an account?
                            <span
                                className="signup-link"
                                onClick={() => navigate("/signup")}
                            >
                                Sign up here
                            </span>
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}

export default ReviewForm;